import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Scale, ArrowLeft, CheckCircle, XCircle, Clock, Loader2,
  ChevronDown, ChevronUp, FileText, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { AuthContext } from "@/App";

// Use relative URL in production (deployed), or env variable in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const WorkflowExecution = () => {
  const navigate = useNavigate();
  const { executionId } = useParams();
  const { user } = useContext(AuthContext);
  
  const [execution, setExecution] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSteps, setExpandedSteps] = useState({});

  useEffect(() => {
    fetchExecution();
    
    // Poll for updates if running
    const interval = setInterval(() => {
      if (execution?.status === "running") {
        fetchExecution(false);
      }
    }, 3000);
    
    return () => clearInterval(interval);
  }, [executionId, execution?.status]);

  const fetchExecution = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/workflows/executions/${executionId}`);
      setExecution(res.data);
      
      // Fetch workflow details
      if (res.data.workflow_id) {
        const wfRes = await axios.get(`${BACKEND_URL}/api/workflows/${res.data.workflow_id}`);
        setWorkflow(wfRes.data);
      }
    } catch (error) {
      console.error("Error fetching execution:", error);
      toast.error("Failed to load execution");
    } finally {
      setLoading(false);
    }
  };

  const toggleStep = (index) => {
    setExpandedSteps(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: "bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30",
      running: "bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30",
      failed: "bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30",
      pending: "bg-[#64748B]/20 text-[#64748B] border-[#64748B]/30",
      skipped: "bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30"
    };
    return colors[status] || colors.pending;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle className="w-5 h-5 text-[#10B981]" />;
      case "running": return <Loader2 className="w-5 h-5 text-[#3B82F6] animate-spin" />;
      case "failed": return <XCircle className="w-5 h-5 text-[#EF4444]" />;
      case "skipped": return <Clock className="w-5 h-5 text-[#F59E0B]" />;
      default: return <Clock className="w-5 h-5 text-[#64748B]" />;
    }
  };

  const getProgress = () => {
    if (!execution) return 0;
    const total = execution.total_steps || 1;
    const current = execution.current_step || 0;
    return Math.round((current / total) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!execution) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-[#EF4444] mx-auto mb-4" />
          <h3 className="font-playfair text-lg text-[#E2E8F0]">Execution Not Found</h3>
          <Button 
            onClick={() => navigate("/workflows")}
            className="mt-4 bg-[#C5A059] text-[#0B0C10]"
          >
            Back to Workflows
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="workflow-execution-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/workflows")}
              data-testid="back-btn"
              className="text-[#94A3B8] hover:text-[#E2E8F0]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Scale className="w-6 h-6 text-[#C5A059]" />
              <div>
                <span className="font-playfair text-lg font-semibold text-[#E2E8F0]">
                  {execution.workflow_name}
                </span>
                <p className="text-[#64748B] text-xs font-mono">
                  {execution.execution_id}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge className={`${getStatusColor(execution.status)} font-mono`}>
              {execution.status.toUpperCase()}
            </Badge>
            {execution.status === "running" && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => fetchExecution(false)}
                className="border-white/20 text-[#E2E8F0]"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          {/* Progress */}
          <Card className="bg-[#15171C] border-white/5 rounded-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[#E2E8F0] font-medium">Progress</span>
              <span className="text-[#C5A059] font-mono text-sm">
                {execution.current_step}/{execution.total_steps} steps
              </span>
            </div>
            <Progress value={getProgress()} className="h-2 bg-[#0B0C10]" />
            
            <div className="flex items-center justify-between mt-4 text-sm text-[#94A3B8]">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Started: {new Date(execution.started_at).toLocaleString()}
              </div>
              {execution.completed_at && (
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Completed: {new Date(execution.completed_at).toLocaleString()}
                </div>
              )}
            </div>
            
            {execution.error && (
              <div className="mt-4 p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-sm">
                <p className="text-[#EF4444] text-sm font-mono">{execution.error}</p>
              </div>
            )}
          </Card>

          {/* Steps */}
          <h3 className="font-playfair text-lg text-[#E2E8F0] mb-4">Workflow Steps</h3>
          
          <div className="space-y-3">
            {workflow?.steps?.map((step, index) => {
              const stepResult = execution.step_results?.find(r => r.step_index === index);
              const status = stepResult?.status || (index < execution.current_step ? "completed" : index === execution.current_step && execution.status === "running" ? "running" : "pending");
              const isExpanded = expandedSteps[index];
              
              return (
                <Collapsible key={step.step_id} open={isExpanded} onOpenChange={() => toggleStep(index)}>
                  <Card className={`bg-[#15171C] border-white/5 rounded-sm overflow-hidden ${status === "running" ? "border-[#3B82F6]/30" : ""}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${getStatusColor(status)}`}>
                          {getStatusIcon(status)}
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <span className="text-[#64748B] font-mono text-xs">Step {index + 1}</span>
                            <Badge className={`${getStatusColor(status)} font-mono text-xs`}>
                              {status}
                            </Badge>
                          </div>
                          <h4 className="text-[#E2E8F0] font-medium">{step.name}</h4>
                          <p className="text-[#64748B] text-sm">{step.description}</p>
                        </div>
                      </div>
                      
                      {stepResult && (
                        isExpanded ? 
                          <ChevronUp className="w-5 h-5 text-[#64748B]" /> : 
                          <ChevronDown className="w-5 h-5 text-[#64748B]" />
                      )}
                    </CollapsibleTrigger>
                    
                    {stepResult?.result && (
                      <CollapsibleContent>
                        <div className="px-4 pb-4 border-t border-white/5 pt-4">
                          <h5 className="text-[#94A3B8] text-xs font-mono uppercase mb-3">Result</h5>
                          <ScrollArea className="max-h-[400px]">
                            <div className="bg-[#0B0C10] rounded-sm p-4">
                              {Object.entries(stepResult.result).map(([key, value]) => {
                                if (key === "status" || !value) return null;
                                
                                return (
                                  <div key={key} className="mb-4 last:mb-0">
                                    <h6 className="text-[#C5A059] text-xs font-mono uppercase mb-2">
                                      {key.replace(/_/g, ' ')}
                                    </h6>
                                    {typeof value === "string" ? (
                                      <pre className="text-[#E2E8F0] text-sm whitespace-pre-wrap font-manrope">
                                        {value}
                                      </pre>
                                    ) : Array.isArray(value) ? (
                                      <div className="space-y-2">
                                        {value.slice(0, 10).map((item, i) => (
                                          <div key={i} className="p-2 bg-[#15171C] rounded-sm">
                                            {typeof item === "object" ? (
                                              <div>
                                                {item.citation && (
                                                  <p className="text-[#C5A059] font-mono text-xs">{item.citation}</p>
                                                )}
                                                {item.name && (
                                                  <p className="text-[#E2E8F0] text-sm">{item.name}</p>
                                                )}
                                                {item.title && (
                                                  <p className="text-[#E2E8F0] text-sm">{item.title}</p>
                                                )}
                                              </div>
                                            ) : (
                                              <p className="text-[#E2E8F0] text-sm">{String(item)}</p>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ) : typeof value === "object" ? (
                                      <pre className="text-[#E2E8F0] text-xs font-mono">
                                        {JSON.stringify(value, null, 2)}
                                      </pre>
                                    ) : (
                                      <p className="text-[#E2E8F0] text-sm">{String(value)}</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </ScrollArea>
                        </div>
                      </CollapsibleContent>
                    )}
                  </Card>
                </Collapsible>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowExecution;
